import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  InputAdornment,
  Autocomplete,
  Stack,
  Paper,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { NumericFormat } from "react-number-format";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { ManualExpenseEndPointLogic } from "../../../../../logic/Finance/ProviderExpense/ManualExpenseEndpointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import { useManualExpenseValidation } from "../../../../../customHooks/useManualExpenseValidation";

export const EditExpense = ({ open, handleClose, expenseData }: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const userData = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const currency = useSelector((state: any) => state.manualExpense.currencies);
  const allTrafficSources = useSelector(
    (state: any) => state.manualExpense.allTrafficSources
  );
  const [loading, setLoading] = useState(false);
  const [assetSharedUID, setAssetSharedUID] = useState("");
  const [, setSourceUID] = useState("");
  const [date, setDate] = useState("");
  const [impressions, setImpressions] = useState(0);
  const [currencyCode, setCurrencyCode] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [clicks, setClicks] = useState(0);
  const [amount, setAmount] = useState("");
  const [comment, setComment] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const { errors, validate, validateAll, resetErrors } =
    useManualExpenseValidation();
  const trafficSourceUid = searchParams.get("trafficSourceUid");
  const websiteSharedUid = searchParams.get("websiteSharedUid");
  const currencyShortCode = searchParams.get("currencyShortCode");
  const expenseShareUid = searchParams.get("expenseShareUid");
  const selected = searchParams.get("selected");
  const siteNames = userData?.data?.website_permissions?.map(
    (permission: any) => {
      const key = Object.keys(permission)[0];
      return {
        site: permission[key],
        key: key,
      };
    }
  );

  useEffect(() => {
    if (open) {
      ManualExpenseEndPointLogic.getTrafficSource(oauthTokenHeader);
      ManualExpenseEndPointLogic.getCurrency(oauthTokenHeader, dispatch);
    }
    // eslint-disable-next-line
  }, [open, oauthTokenHeader]);

  useEffect(() => {
    if (open && expenseData) {
      setAssetSharedUID(expenseData.assetSharedUID);
      setSourceUID(expenseData.sourceUID);
      setDate(expenseData.date);
      setImpressions(expenseData.impressions);
      setCurrencyCode(expenseData.currencyCode);
      setClicks(expenseData.clicks);
      setAmount(expenseData.expenses);
      setComment(expenseData.expenseName);
      setHasChanges(false);
      resetErrors();
    }
    // eslint-disable-next-line
  }, [open, expenseData]);

  useEffect(() => {
    const initialCurrency = currency?.find(
      (c: any) => c.currency_code === currencyShortCode
    );
    const initialCurrencySharedUid = currency.find(
      (c: any) => c.currency_code === currencyShortCode
    ) as any;
    setSelectedCurrency(initialCurrency);
    setCurrencyCode(initialCurrencySharedUid?.shared_uid || "");
    setCurrencySymbol(initialCurrency?.currency_symbol || "");
  }, [currencyShortCode, currency]);

  const checkForChanges = () => {
    if (!expenseData) return false;
    const currentDate = date ? dayjs(date).format("YYYY-MM-DD") : "";
    const originalDate = expenseData.date
      ? dayjs(expenseData.date).format("YYYY-MM-DD")
      : "";
    const hasChanged =
      Number(impressions) !== Number(expenseData.impressions) ||
      Number(clicks) !== Number(expenseData.clicks) ||
      Number(amount) !== Number(expenseData.expenses) ||
      comment !== expenseData.expenseName ||
      currentDate !== originalDate ||
      currencyCode !== expenseData.currencyCode;
    setHasChanges(hasChanged);
    return hasChanged;
  };

  const handleChangeCurrency = (_: unknown, option: any) => {
    setSelectedCurrency(option); // Update the selected currency
    setCurrencyCode(option?.shared_uid); // Assuming setCurrencyCode is defined elsewhere
    setCurrencySymbol(option?.currency_symbol || ""); // Update the currency symbol
    setTimeout(() => checkForChanges(), 0); // Check for changes after state updates
  };

  const handleCancel = () => {
    handleClose();
    setHasChanges(false);
    resetErrors();
  };

  const handleAlertClose = () => {
    setAlertOpen(false); // Close Snackbar
  };

  useEffect(() => {
    if (alertOpen) {
      const timer = setTimeout(() => {
        setAlertOpen(false);
      }, 3000);
      return () => clearTimeout(timer); // Cleanup timer when component unmounts or alertOpen changes
    }
  }, [alertOpen]);

  const handleEditExpense = async () => {
    setLoading(true);
    const updateData = {
      website_shared_uid: assetSharedUID,
      currency: currencyCode,
      date: date ? dayjs(date).format("YYYY-MM-DD") : "",
      impressions: Number(impressions) || 0,
      clicks: Number(clicks),
      expenses: Number(amount),
      comment: comment,
    };
    const isValid = validateAll({ impressions, clicks, amount, comment });
    if (!isValid) {
      setAlertOpen(true); // show warning alert
      return; // prevent submission
    }
    try {
      const res = await ManualExpenseEndPointLogic.updateManualExpenseData(
        oauthTokenHeader,
        updateData,
        expenseShareUid
      );
      if (res?.status !== 200) {
        setAlertOpen(true);
        setSnackbarMessage("Error: " + res?.reason);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error updating expense:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unable to edit expense",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
      handleClose();
      resetErrors();
      setSearchParams((prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);
        updatedParams.delete("expenseShareUid");
        updatedParams.delete("expenseName");
        updatedParams.delete("websiteName");
        updatedParams.delete("trafficSourceUid");
        updatedParams.delete("date");
        updatedParams.delete("clicks");
        updatedParams.delete("impressions");
        updatedParams.delete("expenses");
        updatedParams.delete("createdBy");
        return updatedParams;
      });
      if (selected) {
        navigate(
          `/performance/manual_expenses?selected=${selected}`
        );
      } else {
        navigate("/performance/manual_expenses");
      }
    }
  };

  const renderSnackbar = () => {
    return (
      <Snackbar
        sx={{ zIndex: "9999999" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarMessage.includes("Error") ? "error" : "success"}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  };

  return (
    <>
      {renderSnackbar()}
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Dialog
            open={open}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
            PaperProps={{
              sx: { width: "900px", maxWidth: "900px", overflowY: "auto" },
            }}
          >
            <Paper
              sx={{
                padding: "24px 24px 20px 24px",
                maxHeight: "80vh",
                overflowY: "auto",
              }}
              ref={dialogContentRef}
            >
              {alertOpen && (
                <Alert
                  onClose={handleAlertClose}
                  severity="warning"
                  sx={{ width: "100%" }}
                >
                  All mandatory fields are required!
                </Alert>
              )}
              <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
                Edit Expense
              </DialogTitle>
              <Typography sx={{ marginBottom: "16px" }}>Details</Typography>
              <DialogContent sx={{ padding: "8px 0px" }}>
                <Stack flexDirection="row" gap="16px">
                  <Autocomplete
                    options={siteNames}
                    disabled
                    getOptionLabel={(option: any) => option?.site?.ui_site_name}
                    value={siteNames.find(
                      (source: any) => source.key === websiteSharedUid
                    )}
                    onChange={(_: unknown, option: any) =>
                      setAssetSharedUID(option?.key)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Related To"
                        placeholder="Choose Asset from the list."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start"></InputAdornment>
                          ),
                        }}
                        color="secondary"
                        required
                      />
                    )}
                    size="small"
                    sx={{ width: "300px" }}
                  />
                  <Autocomplete
                    options={allTrafficSources}
                    disabled
                    getOptionLabel={(option: any) => option.traffic_source_name}
                    value={
                      allTrafficSources?.find(
                        (source: any) => source.shared_uid === trafficSourceUid
                      ) || null
                    }
                    onChange={(_, option: any) =>
                      setSourceUID(option?.shared_uid)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Related To"
                        placeholder="Choose Asset from the list."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start"></InputAdornment>
                          ),
                        }}
                        color="secondary"
                        required
                      />
                    )}
                    size="small"
                    sx={{ width: "300px" }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer
                      components={["DatePicker"]}
                      sx={{ paddingTop: "0", overflow: "visible" }}
                    >
                      <DatePicker
                        className="brandDataDatePicker"
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            size: "small",
                            color: "secondary",
                            required: true,
                          },
                        }}
                        value={date ? dayjs(date) : null}
                        sx={{ width: "300px" }}
                        label="Pick a date"
                        onChange={(date) => {
                          if (date) {
                            const formattedDate =
                              dayjs(date).format("YYYY-MM-DD");
                            setDate(formattedDate);
                            setTimeout(() => checkForChanges(), 0);
                          }
                        }}
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                </Stack>
              </DialogContent>
              <DialogContent
                sx={{
                  padding: "0",
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <NumericFormat
                  customInput={TextField}
                  fullWidth
                  size="small"
                  color="secondary"
                  sx={{ mt: "16px", mb: "16px", padding: "0", width: "49%" }}
                  id="outlined-helperText"
                  label="Impressions"
                  value={impressions}
                  thousandSeparator={true}
                  prefix=""
                  decimalScale={0}
                  fixedDecimalScale={false}
                  allowNegative={true}
                  error={Boolean(errors.impressions)}
                  helperText={errors.impressions}
                  onValueChange={(values) => {
                    const { value }: any = values;
                    setImpressions(value);
                    validate("impressions", value);
                    setTimeout(() => checkForChanges(), 0);
                  }}
                />
                <NumericFormat
                  customInput={TextField}
                  fullWidth
                  size="small"
                  color="secondary"
                  sx={{ mt: "16px", mb: "16px", padding: "0", width: "49%" }}
                  id="outlined-helperText"
                  label="Clicks"
                  value={clicks}
                  thousandSeparator={true}
                  prefix=""
                  decimalScale={0}
                  fixedDecimalScale={false}
                  allowNegative={true}
                  error={Boolean(errors.clicks)}
                  helperText={errors.clicks}
                  onValueChange={(values) => {
                    const { value }: any = values;
                    setClicks(value);
                    validate("clicks", value);
                    setTimeout(() => checkForChanges(), 0);
                  }}
                />
              </DialogContent>
              <DialogContent sx={{ padding: "0" }}>
                <Stack flexDirection="row" gap="16px">
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    size="small"
                    color="secondary"
                    sx={{ mt: "16px", mb: "16px", padding: "0", width: "49%" }}
                    id="outlined-helperText"
                    label="Amount"
                    value={amount}
                    thousandSeparator={true}
                    prefix={currencySymbol ? `${currencySymbol} ` : ""}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    allowNegative={true}
                    error={Boolean(errors.amount)}
                    helperText={errors.amount}
                    onValueChange={(values) => {
                      const { value }: any = values;
                      setAmount(value);
                      validate("amount", value);
                      setTimeout(() => checkForChanges(), 0);
                    }}
                  />
                  <Autocomplete
                    options={currency || []}
                    getOptionLabel={(option: any) =>
                      option
                        ? `${option.currency_symbol} ${option.currency_name}`
                        : ""
                    }
                    value={selectedCurrency}
                    onChange={handleChangeCurrency}
                    renderOption={(props, option: any) => (
                      <li {...props}>
                        {/* Add the currency symbol next to the currency name */}
                        <span style={{ marginRight: 8 }}>
                          {option?.currency_symbol}
                        </span>
                        {option?.currency_name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Currency"
                        placeholder="Choose Currency from the list."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start"></InputAdornment>
                          ),
                        }}
                        color="secondary"
                        required
                      />
                    )}
                    size="small"
                    sx={{
                      width: "49%",
                      marginTop: "16px",
                      marginBottom: "16px",
                    }}
                  />
                </Stack>
              </DialogContent>
              <Typography
                sx={{
                  marginBottom: "16px",
                  marginTop: "8px",
                  fontSize: "12px",
                  lineHeight: "20px",
                  color: "#00000099",
                }}
              >
                Fields marked with * are mandatory.
              </Typography>
              <Typography mt="16px" mb="8px">
                Expense Description
              </Typography>
              <TextField
                id="outlined-multiline-flexible"
                label="Statement" // Set your label here
                placeholder="Add your description here" // Set your placeholder here
                multiline
                color="secondary"
                size="medium"
                required
                maxRows={4}
                error={Boolean(errors.comment)}
                helperText={errors.comment}
                sx={{
                  width: "852px",
                }}
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  validate("comment", e.target.value);
                  setTimeout(() => checkForChanges(), 0);
                }}
              />
            </Paper>
            <Paper sx={{ padding: "16px 24px 20px 24px" }}>
              <DialogActions sx={{ padding: "16px 0 0 0" }}>
                <Button onClick={handleCancel} color="secondary">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleEditExpense}
                  disabled={
                    loading ||
                    !impressions ||
                    !clicks ||
                    !date ||
                    !amount ||
                    !currencyCode ||
                    !comment ||
                    !hasChanges ||
                    Object.values(errors).some((error) => error !== "")
                  }
                >
                  Save{" "}
                  <CheckOutlinedIcon
                    fontSize="small"
                    sx={{ marginLeft: "8px" }}
                  />
                </Button>
              </DialogActions>
            </Paper>
          </Dialog>
        </>
      )}
    </>
  );
};
